import { MapState } from 'src/app/interfaces/map.interface';
import { FullUser, SearchResult, UserRole } from 'src/app/swagger-api';
import { MapFilters } from '../../map.enum';
import MarkersMappers from '../markers.mappers';
import { isUserEntityKind } from './decorators.helpers';
import { NonAggregationProcessorType, SharedResponseProcessorDecorator } from './shared-response-processor.decorator';

export class UserResponseProcessorDecorator extends SharedResponseProcessorDecorator<SearchResult> {
  public constructor(entity: NonAggregationProcessorType, state: MapState) {
    super(entity, state);
  }

  public getProcessedItems(): SearchResult[] {
    let results: SearchResult[] = [];
    this.entity.getProcessedItems().forEach((result: SearchResult) => {
      if (!isUserEntityKind(result)) {
        results.push(result);
        return;
      }

      results = this.getProcessedUserSearchResult(results, result);
    });

    return results;
  }

  /**
   * @description Removes user roles that don't included in state roles list.
   * @param { FullUser } user
   * @return { FullUser }
   */
  private getClearedUserByRolesFilter(user: FullUser): FullUser {
    if (this.state.roles && this.state.roles.length) {
      const mappedRoles: UserRole[] = this.state.roles.map(
        (role: string) => MarkersMappers.nestedBucketIndividualNameToRoleMapper[role] as UserRole,
      );

      user.user_roles = user.user_roles.filter((role: UserRole) => mappedRoles.indexOf(role) !== -1);
    }

    return user;
  }

  /**
   * @description Removes user roles that don't have such items in view description.
   * @param { FullUser } user
   * @param { string[] } listToFilter
   * @param { string } mapperName
   * @return { FullUser }
   */
  private getClearedUserByListFilter<T extends string>(
    user: FullUser,
    listToFilter: string[],
    mapper: { [key: string]: (user: FullUser) => T[] },
  ): FullUser {
    if (listToFilter && listToFilter.length) {
      user.user_roles = user.user_roles.filter(
        (role: string) =>
          mapper[role](user).filter((item: string) => listToFilter.indexOf(item) !== -1).length === listToFilter.length,
      );
    }

    return user;
  }

  /**
   * @description Updates user roles list according to map state filters.
   * @param { FullUser } user
   * @return { FullUser }
   */
  private getProcessedIndividualUserSearchResult(user: FullUser): FullUser {
    if (this.state.filter === MapFilters.PEOPLE) {
      user = this.getClearedUserByListFilter<string>(
        this.getClearedUserByListFilter<string>(
          this.getClearedUserByRolesFilter(user),
          this.state.diseases,
          MarkersMappers.diseasesPropRoleMapper,
        ),
        this.state.specialties,
        MarkersMappers.specialtiesPropRoleMapper,
      );
    }

    return user;
  }

  private getProcessedUserSearchResult(results: SearchResult[], result: SearchResult): SearchResult[] {
    let user: FullUser = result.item as FullUser;
    if (user.user_roles.length === 1 || user.user_roles.includes(UserRole.Organization)) {
      results.push(result);
      return results;
    }

    user = this.getProcessedIndividualUserSearchResult(user);

    return results.concat(
      user.user_roles.map((role: UserRole) => ({
        ...result,
        item: { ...user, user_roles: [role] },
      })),
    );
  }
}
